import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authService } from '@/api/services/auth.service';
import type { LoginInput, UserProfile } from '@bstock/shared';
import type { ModuleAccess } from '@bill/core-types';

interface AuthContextType {
  user: UserProfile | null;
  /**
   * Acces module sur la brand active (US-MAC).
   *  - `null` quand pas charge / backend legacy
   *  - Sinon : { availableProducts, roleByProduct, bdisplayScreenLimit, ... }
   * Utilise par AppSwitcher / UI conditional / pages pour cacher modules.
   */
  moduleAccess: ModuleAccess | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSwitchingBrand: boolean;
  login: (data: LoginInput) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  switchBrand: (brandId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [moduleAccess, setModuleAccess] = useState<ModuleAccess | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitchingBrand, setIsSwitchingBrand] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Charger l'utilisateur au montage si un token existe
  useEffect(() => {
    const loadUser = async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        try {
          const meData = await authService.me();
          setUser(meData.user);
          setModuleAccess(meData.moduleAccess);
        } catch {
          // Token invalide ou expire, nettoyer le storage
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
      setIsLoading(false);
    };

    loadUser();
  }, []);

  const login = useCallback(
    async (data: LoginInput): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await authService.login(data);

        // Stocker les tokens
        localStorage.setItem('accessToken', response.tokens.accessToken);
        localStorage.setItem('refreshToken', response.tokens.refreshToken);

        // Mettre a jour l'utilisateur
        setUser(response.user);

        // Recuperer aussi le moduleAccess (US-MAC) pour filtrer l'AppSwitcher
        // et conditionner l'UI des le 1er render. Best-effort : si l'endpoint
        // n'a pas encore le champ (legacy), moduleAccess restera null.
        try {
          const me = await authService.me();
          setModuleAccess(me.moduleAccess);
        } catch {
          /* ignore — le moduleAccess sera charge au prochain refresh */
        }

        toast.success('Connexion reussie');
        navigate('/');
        return { success: true };
      } catch (error: unknown) {
        const axiosError = error as { response?: { status?: number; data?: { error?: { message?: string } } } };
        let message: string;
        if (!axiosError.response) {
          message = 'Service temporairement indisponible';
        } else {
          message = axiosError.response.data?.error?.message || 'Erreur de communication avec le serveur';
        }
        toast.error(message);
        return { success: false, error: message };
      }
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch (error) {
      console.error('Erreur lors de la deconnexion:', error);
    } finally {
      // Nettoyer le state et le storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setModuleAccess(null);

      toast.success('Deconnexion reussie');
      navigate('/login');
    }
  }, [navigate]);

  const switchBrand = useCallback(async (brandId: string) => {
    if (!user) return;

    // Ne pas switcher vers la meme brand
    if (user.currentBrand.id === brandId) return;

    setIsSwitchingBrand(true);
    try {
      // Appel API pour obtenir de nouveaux tokens
      const tokens = await authService.switchBrand(brandId);

      // Mettre a jour les tokens dans localStorage
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);

      // Recharger le profil utilisateur pour avoir la nouvelle brand active
      const meData = await authService.me();
      setUser(meData.user);
      setModuleAccess(meData.moduleAccess);

      // Invalider tout le cache React Query pour la nouvelle brand
      await queryClient.invalidateQueries();

      toast.success(`Basculement vers ${meData.user.currentBrand.name}`);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: { message?: string } } } };
      let message: string;
      if (!axiosError.response) {
        message = 'Service temporairement indisponible';
      } else {
        message = axiosError.response.data?.error?.message || 'Erreur lors du changement de brand';
      }
      toast.error(message);
      throw error;
    } finally {
      setIsSwitchingBrand(false);
    }
  }, [user, queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        moduleAccess,
        isAuthenticated: !!user,
        isLoading,
        isSwitchingBrand,
        login,
        logout,
        switchBrand,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit etre utilise dans un AuthProvider');
  }
  return context;
}
